import { useState } from "react";
import { ShieldCheckIcon, WarningIcon } from "@phosphor-icons/react";

const FLAGS = [
  { key: "wa_dedupe", label: "WA_DEDUPE", desc: "90-day intro dedupe per number, 24h cross-group duplicate = one alert, stop after 2 unanswered." },
  { key: "wa_keyword_tuning", label: "Keyword tuning", desc: "Apply Grok's negative keywords and the admin-post penalty to live scoring." },
  { key: "wa_replies", label: "Bot replies", desc: "Let Grok's replies and approved drafts go out through the current listener.", risky: true },
  { key: "wa_forward_replies", label: "WA_FORWARD_REPLIES", desc: "Attach private replies from leads you messaged to their thread. Needs the updated VPS listener." },
  { key: "wa_followups", label: "WA_FOLLOWUPS", desc: "AI-written day-2 and day-7 follow-ups to unanswered intros, max 2, then no_response. Quiet hours 20–08 local." },
  { key: "wa_daily_digest", label: "WA_DAILY_DIGEST", desc: "One lead digest at 07:00 Ljubljana, needs-answer first. When live it replaces the old daily digest." },
];

function Switch({ on, onClick, testId, disabled, label }) {
  return (
    <button data-testid={testId} disabled={disabled} onClick={onClick} aria-checked={on} role="switch" aria-label={label}
      className={`relative inline-flex h-6 w-11 items-center transition-colors flex-shrink-0 disabled:opacity-40 ${on ? "bg-zinc-950" : "bg-zinc-300"}`}>
      <span className={`inline-block h-4 w-4 transform bg-white transition-transform ${on ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

export function FlagsPanel({ flags, onSave, busy }) {
  const [confirming, setConfirming] = useState(null);
  const dry = flags.dry_run;

  const request = (key, value) => {
    const risky = (key === "dry_run" && value === false) || (key === "wa_replies" && value === true);
    if (risky && confirming !== key) return setConfirming(key);
    setConfirming(null);
    onSave({ [key]: value });
  };

  return (
    <div className="mb-6" data-testid="grok-flags-panel">
      <div data-testid="grok-dryrun-banner"
        className={`border-2 p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4 ${dry ? "border-zinc-950 bg-white" : "border-[#FF3B30] bg-[#FF3B30]/5"}`}>
        <div className={`w-11 h-11 flex-shrink-0 flex items-center justify-center text-white ${dry ? "bg-zinc-950" : "bg-[#FF3B30]"}`}>
          {dry ? <ShieldCheckIcon size={24} weight="fill" /> : <WarningIcon size={24} weight="fill" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-black text-base">{dry ? "Dry-run mode — nothing is sent or blocked" : "LIVE — enabled flags now act on real messages"}</div>
          <div className="text-sm text-zinc-500 mt-0.5">
            Every decision is still logged so the report below shows what would have happened.
          </div>
        </div>
        <button data-testid="grok-dryrun-toggle" disabled={busy} onClick={() => request("dry_run", !dry)}
          className={`px-5 py-2.5 text-xs font-bold uppercase tracking-wider disabled:opacity-50 transition-colors ${confirming === "dry_run" ? "bg-[#FF3B30] text-white" : dry ? "bg-white border border-zinc-300 hover:bg-zinc-50" : "bg-zinc-950 text-white hover:bg-zinc-800"}`}>
          {confirming === "dry_run" ? "Tap again to go live" : dry ? "Go live" : "Back to dry-run"}
        </button>
      </div>

      <div className="border border-zinc-200 bg-white divide-y divide-zinc-100">
        {FLAGS.map((f) => (
          <div key={f.key} className="flex items-center gap-4 p-4" data-testid={`grok-flag-row-${f.key}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                <span className="font-mono-num">{f.label}</span>
                <span data-testid={`grok-flag-state-${f.key}`} className={`px-1.5 py-0.5 text-[10px] uppercase tracking-wider font-bold ${flags[f.key] ? (dry ? "bg-[#FFCC00] text-zinc-900" : "bg-emerald-500 text-white") : "bg-zinc-200 text-zinc-600"}`}>
                  {flags[f.key] ? (dry ? "On · dry-run" : "On · live") : "Off · observing"}
                </span>
                {f.soon && <span className="text-[10px] uppercase tracking-wider text-zinc-400">next round</span>}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{f.desc}</div>
              {confirming === f.key && <div className="text-xs text-[#FF3B30] mt-1 font-semibold">Tap the switch again to confirm.</div>}
              {f.key === "wa_followups" && (
                <label className="flex items-center gap-2 mt-2 text-xs text-zinc-600" data-testid="grok-followups-start-date-row">
                  <span className="font-mono-num font-semibold">WA_FOLLOWUPS_START_DATE</span>
                  <input type="date" data-testid="grok-followups-start-date-input" disabled={busy}
                    defaultValue={flags.followups_start_date} key={flags.followups_start_date}
                    onChange={(e) => e.target.value && onSave({ followups_start_date: e.target.value })}
                    className="border border-zinc-300 px-2 py-1 text-xs font-mono-num bg-white" />
                  <span className="text-zinc-400">00:00 Ljubljana · older intros are never followed up or forwarded</span>
                </label>
              )}
            </div>
            <Switch on={!!flags[f.key]} disabled={busy} testId={`grok-flag-${f.key}`} label={`Toggle ${f.label}`} onClick={() => request(f.key, !flags[f.key])} />
          </div>
        ))}
      </div>
    </div>
  );
}
